import React from "react";
import { Slider, SlideShowTransitions } from "../reusable/components";
import imagePlaceholder from "../reusable/images/ImagePlaceholder.svg";
import { dispatchEvent, EventMaker } from "../trigger/index";
import { Embed } from "../spreadsheet/providers/video";
import TwitchIntro from "./templates/twitchIntro";
import Idle from "./templates/idle";
import TwitterIdle from "./templates/twitterIdle";

export const ItemTypes = {
  ELEMENT_CONTAINER: "element_container",
  ELEMENT_TEXT: "element_text",
  ELEMENT_IMAGE: "element_image",
  ELEMENT_SLIDER: "element_slider",
  ELEMENT_COUNTDOWN: "element_countdown",
  ELEMENT_PROGRESSBAR: "element_progressbar",
  ELEMENT_EMPTYCONTAINER: "element_emptycontainer",
  ELEMENT_VIDEO: "element_video",
  ELEMENT_ICON: "element_icon",
  ELEMENT_CLOCK_DIGITAL: "element_clock_digital",
  ELEMENT_CLOCK_AMPM: "element_clock_ampm",
  ELEMENT_COUNTDOWN_PROGRESS: "element_countdown_progress",
  ELEMENT_GAMEBOX: "element_gamebox",
  ELEMENT_GAMENAME: "element_gamename"
};

export const Labels = {
  [ItemTypes.ELEMENT_TEXT]: "Text",
  [ItemTypes.ELEMENT_IMAGE]: "Image",
  [ItemTypes.ELEMENT_EMPTYCONTAINER]: "Container",
  [ItemTypes.ELEMENT_SLIDER]: "Slider",
  [ItemTypes.ELEMENT_PROGRESSBAR]: "Progress Bar",
  [ItemTypes.ELEMENT_COUNTDOWN]: "Countdown",
  [ItemTypes.ELEMENT_VIDEO]: "Video",
  [ItemTypes.ELEMENT_ICON]: "Icon",
  [ItemTypes.ELEMENT_CLOCK_DIGITAL]: "Digital",
  [ItemTypes.ELEMENT_CLOCK_AMPM]: "Digital am/pm",
  [ItemTypes.ELEMENT_COUNTDOWN_PROGRESS]: "Countdown Progress",
  [ItemTypes.ELEMENT_GAMEBOX]: "Game Box",
  [ItemTypes.ELEMENT_GAMENAME]: "Game Name"
};

export const CollectionProps = {
  collection: "none",
  space: 10,
  direction: "vertical",
  limit: 10,
  skip: 0,
  cycle: false
};

const AnimationProps = {
  enterAnimation: "none",
  leaveAnimation: "none",
  delay: 0,
  transformOrigin: "50% 50% 0"
};

const Effects = {
  opacity: 1,
  perspective: 150,
  rotate: 0,
  rotateX: 0,
  rotateY: 0,
  skewX: 0,
  skewY: 0,
  blur: 0
};

const TriggerProps = {
  showWhen: "",
  stayDuration: 7000,
  showEvery: 0
};

const makeBorderRadius = v => ({
  borderBottomRightRadius: v,
  borderBottomLeftRadius: v,
  borderTopRightRadius: v,
  borderTopLeftRadius: v
});

/********************* Container ***************
 ***********************************************/
const transformProp = (prop, val) => `${prop}(${val})`;
const units = {
  rotateX: "deg",
  rotateY: "deg",
  rotate: "deg",
  perspective: "px",
  skewX: "deg",
  skewY: "deg"
};

const computeTransforms = props =>
  Object.keys(props)
    .filter(p => units[p])
    .filter(p => !(p === "perspective" && props.rotateX === 0 && props.rotateY === 0)) //remove perspective when not needed
    .reduce((acc, p) => acc + transformProp(p, props[p] + units[p]), "");

const ContainerStarter = props => {
  return (
    <div
      style={{
        ...props,
        ...{
          backgroundImage:
            "url(" + props.backgroundImage + ")" + (props.gradient ? ", " + props.gradient : ""),
          overflow: props.hideOverflow ? "hidden" : "visible",
          transform: computeTransforms(props),
          backgroundRepeat: "no-repeat",
          filter: "blur(" + props.blur + "px)",
          perspective: "none"
          // background:
          //   props.backgroundColor2 &&
          //   `linear-gradient(to right, ${props.backgroundColor} 60%, ${props.backgroundColor2})`
        }
      }}>
      {props.children}
    </div>
  );
};

const EmptyContainerStarterProperties = {
  type: ItemTypes.ELEMENT_EMPTYCONTAINER,
  width: 300,
  height: 150,
  top: 0,
  left: 0,
  backgroundColor: "transparent",
  //backgroundColor2: "",
  gradient: "",
  backgroundImage: "",
  backgroundSize: "contain",
  borderColor: "#3B4253",
  borderWidth: 0,
  borderStyle: "solid",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  hideOverflow: false,
  children: [],
  childDelay: 0,
  ...AnimationProps,
  ...CollectionProps,
  ...Effects,
  ...TriggerProps
};

export const defaultCanvas = {
  canvas: {
    width: 600,
    height: 600,
    streamWidth: 16,
    streamHeight: 9,
    left: 0,
    top: 0,
    backgroundImage: "",
    backgroundSize: "contain",
    borderWidth: 0,
    backgroundColor: "transparent",
    type: ItemTypes.ELEMENT_EMPTYCONTAINER,
    id: "canvas",
    label: "Canvas",
    children: [],
    ...AnimationProps
  }
};

/********************* Text ********************
 ***********************************************/
const TextStarter = props => (
  <div
    style={{
      ...props,
      ...{
        userSelect: "none",
        display: "flex",
        justifyContent:
          props.textAlign === "center"
            ? "center"
            : props.textAlign === "right" ? "flex-end" : "flex-start",
        transform: computeTransforms(props),
        perspective: "none"
      }
    }}>
    <div
      style={{
        textOverflow: props.textOverflow === "ellipsis" && "ellipsis",
        overflow:
          (props.textOverflow === "ellipsis" || props.textOverflow === "hidden") && "hidden",
        whiteSpace:
          (props.textOverflow === "ellipsis" || props.textOverflow === "hidden") && "nowrap"
      }}>
      {props.text}
    </div>
  </div>
);

const TextStarterProperties = {
  type: ItemTypes.ELEMENT_TEXT,
  width: 200,
  height: 50,
  top: 0,
  left: 0,
  minWidth: 20,
  minHeight: 20,
  text: "Some Text",
  maxItems: 1,
  fontSize: 30,
  color: "#444",
  letterSpacing: 0,
  fontFamily: "Times New Roman",
  backgroundColor: "transparent",
  borderColor: "#dddddd",
  borderWidth: 0,
  borderStyle: "solid",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  textIdent: 0,
  fontWeight: 400,
  fontStyle: "normal",
  lineHeight: 1.2,
  // padding: 0,
  textAlign: "center",
  alignItems: "center",
  enterAnimation: "none",
  textOverflow: "wrap",
  delay: 0,
  ...AnimationProps,
  ...CollectionProps,
  ...Effects,
  ...TriggerProps
};

/********************* Image *******************
 ***********************************************/
const ImageStarter = props => (
  <img
    style={{
      ...{
        userDrag: "none",
        WebkitUserDrag: "none",
        userSelect: "none",
        display: "block",
        filter: "blur(" + props.blur + "px)",
        transform: computeTransforms(props),
        perspective: "none"
      },
      ...props
    }}
    src={props.url}
    alt="alt"
  />
);

export const ImageStarterProperties = {
  type: ItemTypes.ELEMENT_IMAGE,
  width: 128,
  height: 108,
  top: 0,
  left: 0,
  backgroundColor: "transparent",
  borderColor: "#dddddd",
  borderWidth: 0,
  borderStyle: "solid",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  lockAspectRatio: true,
  url: imagePlaceholder,
  ...TriggerProps,
  ...AnimationProps,
  ...CollectionProps,
  ...Effects
};

const ProgressBarStarter = props => {
  const progPct = props.progress * 100 / props.maximum;
  return (
    <div
      style={{
        width: props.width,
        height: props.height,
        position: "relative"
      }}>
      <div
        style={{
          ...props,
          top: "auto",
          left: "auto",
          width: props.orientation === "vertical" ? "100%" : progPct + "%",
          height: props.orientation === "vertical" ? progPct + "%" : "100%",
          transition: props.orientation === "horizontal" ? "width 1s linear" : "height 1s linear",
          ...(props.orientation === "vertical" && {
            position: "absolute",
            bottom: "0"
          })
        }}
      />
    </div>
  );
};

export const ProgressBarStarterProperties = {
  ...EmptyContainerStarterProperties,
  width: 200,
  height: 11,
  top: 0,
  left: 0,
  progress: 50,
  maximum: 100,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  backgroundColor: "#3A3A3A",
  orientation: "horizontal"
};

/********************* Slider *******************
 ***********************************************/
export const starterSlideProperties = (width, height, slide, color) => ({
  ...EmptyContainerStarterProperties,
  width: width,
  height: height,
  top: 0,
  left: 0,
  backgroundColor: color || "transparent",
  type: ItemTypes.ELEMENT_EMPTYCONTAINER,
  locked: true,
  label: "Slide " + slide,
  children: [
    {
      ...TextStarterProperties,
      width: 100,
      height: 80,
      text: "Slide " + slide,
      left: width / 2 - 50,
      top: height / 2 - 40,
      type: ItemTypes.ELEMENT_TEXT
    }
  ]
});

const SliderStarter = props => (
  <div style={{ overflow: "hidden", width: props.width, height: props.height, ...props }}>
    <Slider
      duration={props.slidingDuration}
      transition={props.transition}
      width={props.width}
      height={props.height}
      slide={props.slide}
      preview={props.preview}>
      {props.children}
    </Slider>
  </div>
);

const SliderStarterProperties = {
  ...EmptyContainerStarterProperties,
  width: 400,
  height: 300,
  top: 0,
  left: 0,
  slidingDuration: 600,
  idleDuration: 10000,
  transition: SlideShowTransitions.Fade,
  slide: 1,
  children: [starterSlideProperties(400, 300, 1), starterSlideProperties(400, 300, 2)]
};

/********************* Countdown ***************
 ***********************************************/
const CountdownStarterProperties = {
  ...TextStarterProperties,
  text: "{countdown.minute}:{countdown.second}"
};

const ClockDigitalStarterProperties = {
  ...TextStarterProperties,
  text: "{clock.hour}:{clock.minute}:{clock.second}"
};

const ClockAMPMStarterProperties = {
  ...TextStarterProperties,
  text: "{clock.h}:{clock.minute} {clock.meridiem}"
};
/********************* POLL ********************
 ***********************************************/
// const PollStarterProperties = {
//   ...EmptyContainerStarterProperties
// };
/********************* ICON ********************
 ***********************************************/
const IconStarterProperties = {
  width: 60,
  height: 60,
  top: 0,
  left: 0,
  icon: "rocket",
  color: "#444444",
  backgroundColor: "transparent",
  lockAspectRatio: true,
  borderColor: "#dddddd",
  borderWidth: 0,
  borderStyle: "solid",
  ...makeBorderRadius(0),
  ...AnimationProps,
  ...Effects,
  ...TriggerProps
};

const IconStarter = props => (
  <div style={{ ...props, textAlign: "center", perspective: "none" }}>
    <i
      className={`fa fa-${props.icon}`}
      style={{ fontSize: props.width - 2 * props.borderWidth + "px" }}
    />
  </div>
);

/********************* VIDEO ***************
 ***********************************************/
const VideoStarterProperties = {
  width: 400,
  height: 300,
  top: 0,
  left: 0,
  url:
    "https://firebasestorage.googleapis.com/v0/b/evolve-f8968.appspot.com/o/big-buck-bunny_trailer.webm?alt=media&token=eda70213-541e-43a1-938a-83908a325475",
  loop: true,
  backgroundColor: "transparent",
  borderColor: "#dddddd",
  borderWidth: 0,
  borderStyle: "solid",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  ...AnimationProps,
  ...Effects,
  ...TriggerProps
};

const videoEnded = id => () => dispatchEvent(EventMaker.videoFinished(id));

const VideoStarter = props =>
  Embed(props) || (
    <video
      key={props.url}
      style={props}
      autoPlay
      muted
      loop={props.loop}
      onEnded={videoEnded(props.id)}>
      <source src={props.url} type="video/webm" />
      Browser does not support the video tag.
    </video>
  );

const GameNameStarterProperties = {
  ...TextStarterProperties,
  text: "{twitch.game}",
  width: 300
};

const GameBoxStarterProperties = {
  ...EmptyContainerStarterProperties,
  width: 240,
  height: 255,
  top: 0,
  left: 0,
  children: [
    {
      ...ImageStarterProperties,
      width: 147,
      height: 206,
      url: "{twitch.game_picture}",
      left: 20,
      top: 20,
      rotateY: -12
    },
    {
      ...EmptyContainerStarterProperties,
      width: 32,
      height: 218,
      backgroundImage: "{twitch.game_picture}",
      backgroundSize: "cover",
      left: 172,
      top: 14,
      rotateY: 28
    }
  ]
};

export const Templates = {
  Intro: "Intro",
  Idle: "Idle",
  TwitterIdle: "TwitterIdle"
};

export const TemplatesBlocks = {
  [Templates.Intro]: TwitchIntro,
  [Templates.Idle]: Idle,
  [Templates.TwitterIdle]: TwitterIdle
};

/********************* DnD *********************
 ***********************************************/
export function starterComponent(type) {
  return {
    [ItemTypes.ELEMENT_TEXT]: TextStarter,
    [ItemTypes.ELEMENT_IMAGE]: ImageStarter,
    [ItemTypes.ELEMENT_PROGRESSBAR]: ProgressBarStarter,
    [ItemTypes.ELEMENT_EMPTYCONTAINER]: ContainerStarter,
    [ItemTypes.ELEMENT_SLIDER]: SliderStarter,
    [ItemTypes.ELEMENT_COUNTDOWN]: TextStarter,
    [ItemTypes.ELEMENT_VIDEO]: VideoStarter,
    [ItemTypes.ELEMENT_ICON]: IconStarter,
    [ItemTypes.ELEMENT_CLOCK_DIGITAL]: TextStarter,
    [ItemTypes.ELEMENT_CLOCK_AMPM]: TextStarter,
    [ItemTypes.ELEMENT_GAMEBOX]: ContainerStarter,
    [ItemTypes.ELEMENT_GAMENAME]: TextStarter
  }[type];
}

export function starterProperties(type) {
  return {
    [ItemTypes.ELEMENT_TEXT]: TextStarterProperties,
    [ItemTypes.ELEMENT_IMAGE]: ImageStarterProperties,
    [ItemTypes.ELEMENT_PROGRESSBAR]: ProgressBarStarterProperties,
    [ItemTypes.ELEMENT_EMPTYCONTAINER]: EmptyContainerStarterProperties,
    [ItemTypes.ELEMENT_SLIDER]: SliderStarterProperties,
    [ItemTypes.ELEMENT_COUNTDOWN]: CountdownStarterProperties,
    [ItemTypes.ELEMENT_VIDEO]: VideoStarterProperties,
    [ItemTypes.ELEMENT_ICON]: IconStarterProperties,
    [ItemTypes.ELEMENT_CLOCK_DIGITAL]: ClockDigitalStarterProperties,
    [ItemTypes.ELEMENT_CLOCK_AMPM]: ClockAMPMStarterProperties,
    [ItemTypes.ELEMENT_GAMEBOX]: GameBoxStarterProperties,
    [ItemTypes.ELEMENT_GAMENAME]: GameNameStarterProperties
  }[type];
}

export const propertyDefault = (type, prop) => starterProperties(type)[prop];



// WEBPACK FOOTER //
// ./src/constants/elements.js